import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge } from '@img/ui';
import { useSession } from '../lib/auth-client';
import { api, type ApiSuccess } from '../lib/api';

interface Wallet {
  balance: number;
  held: number;
  available: number;
  trustedBidder: boolean;
  transactions: Array<{ id: string; type: string; amount: number | string; createdAt: string; reason: string | null }>;
}
interface NotificationItem {
  id: string;
  title: string;
  body: string | null;
  isRead: boolean;
  createdAt: string;
  link?: string | null;
}
interface PropertyItem {
  id: string;
  title: string;
  city: string | null;
  type: string | null;
  status: string;
  price: number | string | null;
  createdAt: string;
}
interface MyAuctionsResp {
  registrations: Array<{ id: string; status: string; auction: { id: string; title: string; status: string } }>;
  bids: Array<{ id: string; amount: number | string; auction: { id: string; title: string } }>;
}
interface AdminStats {
  users: { total: number; new30: number };
  properties: { total: number };
  auctions: { total: number };
}

interface QuotaSummary {
  plan: { code: string; label: string };
  listings: { used: number; quota: number; remaining: number; percentage: number };
  featured: { used: number; quota: number; remaining: number; percentage: number };
  subAccounts: { quota: number };
}

const STATUS_COLORS: Record<string, 'green' | 'amber' | 'red' | 'navy' | 'gray' | 'gold'> = {
  PUBLISHED: 'green',
  DRAFT: 'gray',
  PENDING: 'amber',
  REJECTED: 'red',
  ARCHIVED: 'gray',
  SOLD: 'navy',
};

const STATUS_LABELS: Record<string, string> = {
  PUBLISHED: 'منشور',
  DRAFT: 'مسودة',
  PENDING: 'بانتظار المراجعة',
  REJECTED: 'مرفوض',
  ARCHIVED: 'مؤرشف',
  SOLD: 'مُباع',
};

function StatCard({ label, value, sub, color = 'navy', to }: {
  label: string;
  value: string | number;
  sub?: string;
  color?: 'navy' | 'gold' | 'green' | 'amber';
  to?: string;
}) {
  const tones: Record<string, string> = {
    navy:  'from-navy-dark to-navy text-white',
    gold:  'from-gold to-amber-500 text-white',
    green: 'from-emerald-600 to-emerald-500 text-white',
    amber: 'from-amber-600 to-amber-500 text-white',
  };
  const inner = (
    <Card className={`!bg-gradient-to-br ${tones[color]} ${to ? 'cursor-pointer hover:opacity-95 transition' : ''}`}>
      <CardBody>
        <div className="text-xs opacity-80 mb-1">{label}</div>
        <div className="text-3xl font-black">{value}</div>
        {sub && <div className="text-[11px] opacity-75 mt-1">{sub}</div>}
      </CardBody>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function QuotaBar({ label, used, quota, pct }: { label: string; used: number; quota: number; pct: number }) {
  const isFull = pct >= 100;
  const isWarning = pct >= 80 && !isFull;
  const color = isFull ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs text-ink-secondary font-bold">{label}</span>
        <span className={`text-xs font-black ${isFull ? 'text-red-600' : isWarning ? 'text-amber-700' : 'text-navy-dark'}`}>
          {used} / {quota === 9999 ? '∞' : quota}
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      {isFull && (
        <div className="text-[11px] text-red-600 mt-1 font-bold">⚠️ وصلت للحد الأقصى — رقّ باقتك</div>
      )}
    </div>
  );
}

function ShortcutTile({ to, icon, title, desc }: { to: string; icon: string; title: string; desc: string }) {
  return (
    <Link
      to={to}
      className="group flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:border-gold hover:shadow-md transition"
    >
      <div className="text-2xl shrink-0 group-hover:scale-110 transition">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-navy-dark text-sm">{title}</div>
        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{desc}</div>
      </div>
    </Link>
  );
}

export function DashboardHome() {
  const { data: session } = useSession();
  const user = session?.user as { name: string; role: string; verificationTier?: string; phone?: string; email: string };
  const isAdmin = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';
  const isBroker = user.role === 'BROKER';

  // ⚡ كاش ٣٠ ثانية — يقلّل الطلبات لما يتنقّل المستخدم بين الصفحات ويرجع
  const STALE = 30_000;

  // 💳 المحفظة
  const { data: walletResp } = useQuery({
    queryKey: ['me', 'wallet'],
    queryFn: () => api.get<ApiSuccess<Wallet>>('/api/me/wallet'),
    staleTime: STALE,
  });
  const wallet = walletResp?.data;

  // 🏘️ عقاراتي
  const { data: propsResp } = useQuery({
    queryKey: ['me', 'properties'],
    queryFn: () => api.get<ApiSuccess<PropertyItem[]>>('/api/me/properties'),
    staleTime: STALE,
  });
  const myProperties = propsResp?.data ?? [];

  // 🔨 مزاداتي
  const { data: aucResp } = useQuery({
    queryKey: ['me', 'auctions'],
    queryFn: () => api.get<ApiSuccess<MyAuctionsResp>>('/api/me/auctions'),
    staleTime: STALE,
  });
  const regs = aucResp?.data?.registrations ?? [];
  const bids = aucResp?.data?.bids ?? [];

  // 🔔 الإشعارات
  const { data: notifResp } = useQuery({
    queryKey: ['me', 'notifications'],
    queryFn: () => api.get<ApiSuccess<NotificationItem[]>>('/api/me/notifications'),
    staleTime: STALE,
  });
  const notifications = notifResp?.data ?? [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // 📊 إحصائيات الإدارة (للأدمن فقط)
  const { data: adminStatsResp } = useQuery({
    queryKey: ['admin', 'stats', 'home'],
    queryFn: () => api.get<ApiSuccess<AdminStats>>('/api/admin/stats'),
    enabled: isAdmin,
    staleTime: STALE,
  });
  const adminStats = adminStatsResp?.data;

  // 🎚️ الكوتا (للمسوّق فقط)
  const { data: quotaResp } = useQuery({
    queryKey: ['me', 'quota'],
    queryFn: () => api.get<ApiSuccess<QuotaSummary | null>>('/api/properties/quota'),
    enabled: isBroker,
    staleTime: STALE,
  });
  const quota = quotaResp?.data;

  return (
    <div className="space-y-8">
      {/* رأس الترحيب */}
      <div>
        <h1 className="text-3xl font-black text-navy-dark mb-2">
          مرحباً، {user.name} 👋
        </h1>
        <p className="text-gray-600">
          {isAdmin ? 'لوحة الإدارة بين يديك — كل قسم له تحكم كامل.' :
           isBroker ? 'مرحبا بك مسوّقنا — عقاراتك، طلباتك وعملائك في مكان واحد.' :
           'كل عقاراتك ومزاداتك ومحفظتك في مكان واحد.'}
        </p>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Badge color={isAdmin ? 'gold' : isBroker ? 'navy' : 'navy'}>{user.role}</Badge>
          {user.verificationTier && user.verificationTier !== 'NONE' && (
            <Badge color="green">⭐ {user.verificationTier}</Badge>
          )}
        </div>
      </div>

      {/* 🎚️ ملخّص الكوتا للمسوّق */}
      {isBroker && quota && (
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h3 className="text-base font-black text-navy-dark">📊 استخدام باقتك الحالية</h3>
                <p className="text-xs text-ink-muted">باقة: <b>{quota.plan.label}</b></p>
              </div>
              <Link to="/brokers#plans" className="text-xs font-bold text-gold hover:underline">
                ترقية الباقة ←
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <QuotaBar
                label="الإعلانات الشهرية"
                used={quota.listings.used}
                quota={quota.listings.quota}
                pct={quota.listings.percentage}
              />
              <QuotaBar
                label="الإعلانات المميّزة"
                used={quota.featured.used}
                quota={quota.featured.quota}
                pct={quota.featured.percentage}
              />
            </div>
          </CardBody>
        </Card>
      )}

      {/* CTA — لمسوّق */}
      {isBroker && (
        <Card className="!bg-gradient-to-r from-navy-dark to-navy !text-white !border-gold/30">
          <CardBody>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-black mb-1">💼 ارفع مستوى نشاطك</h3>
                <p className="text-sm opacity-90">رخصة فال، إشعارات الطلبات، وأدوات تسويق احترافية.</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link to="/brokers#plans" className="px-4 py-2 rounded-lg bg-gold text-navy-dark font-bold hover:bg-amber-400 transition text-sm">
                  ترقية الخطة
                </Link>
                <Link to="/dashboard/profile" className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold transition text-sm">
                  رفع رخصة فال
                </Link>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* بطاقات إحصاءات شخصية */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          label="رصيد المحفظة"
          value={wallet ? wallet.available.toLocaleString('en-US') : '—'}
          sub="ر.س متاح للمزايدة"
          color="navy"
          to="/dashboard/wallet"
        />
        <StatCard
          label="عقاراتي"
          value={myProperties.length}
          sub="معروض / مسودة"
          color="gold"
          to="/dashboard/properties"
        />
        <StatCard
          label="مزاداتي"
          value={regs.length}
          sub={`${bids.length} مزايدة`}
          color="green"
          to="/dashboard/auctions"
        />
        <StatCard
          label="إشعارات جديدة"
          value={unreadCount}
          sub={`${notifications.length} كلية`}
          color="amber"
          to="/dashboard/notifications"
        />
      </div>

      {/* لوحة الأدمن: KPIs النظام */}
      {isAdmin && adminStats && (
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-black text-navy-dark">📊 إحصائيات المنصّة</h2>
              <Link to="/admin" className="text-sm font-bold text-gold hover:underline">
                لوحة الإدارة الكاملة ←
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 rounded-lg bg-gray-50">
                <div className="text-2xl font-black text-navy-dark">{adminStats.users.total}</div>
                <div className="text-xs text-gray-500 mt-1">المستخدمون</div>
                <div className="text-[11px] text-emerald-600 font-bold">+{adminStats.users.new30} (٣٠ يوم)</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-gray-50">
                <div className="text-2xl font-black text-navy-dark">{adminStats.properties.total}</div>
                <div className="text-xs text-gray-500 mt-1">العقارات</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-gray-50">
                <div className="text-2xl font-black text-navy-dark">{adminStats.auctions.total}</div>
                <div className="text-xs text-gray-500 mt-1">المزادات</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-gray-50">
                <div className="text-2xl font-black text-navy-dark">{notifications.length}</div>
                <div className="text-xs text-gray-500 mt-1">إشعارات شخصية</div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* اختصارات سريعة */}
      <div>
        <h2 className="text-lg font-black text-navy-dark mb-3">⚡ اختصارات سريعة</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <ShortcutTile to="/properties"  icon="🏘️"  title="تصفّح العقارات"      desc="أحدث العروض في كل المناطق" />
          <ShortcutTile to="/auctions"    icon="🔨"  title="المزادات المباشرة"   desc="انضم للبثوث المباشرة وزايد الآن" />
          <ShortcutTile to="/dashboard/properties" icon="📋" title="عقاراتي"      desc="إضافة، تعديل، إيقاف، إحصاءات" />
          <ShortcutTile to="/dashboard/auctions"   icon="🎯" title="مزاداتي"      desc="تسجيلاتي ومزايداتي وفائزي" />
          <ShortcutTile to="/dashboard/wallet"     icon="💳" title="المحفظة"     desc="الرصيد، شحن، سحب، السجل" />
          <ShortcutTile to="/dashboard/favorites"  icon="⭐"  title="المفضلة"     desc="عقارات ومزادات حفظتها" />
          <ShortcutTile to="/dashboard/alerts"     icon="🔔" title="تنبيهات البحث" desc="إشعار حال نشر عقار يطابق طلبك" />
          <ShortcutTile to="/dashboard/profile"    icon="👤" title="الملف الشخصي" desc="بياناتك، صورة، طلب تغيير معلومات" />
          {isBroker && (
            <ShortcutTile to="/dashboard/subscription" icon="💼" title="اشتراكي"     desc="الخطة الحالية والترقية والفواتير" />
          )}
          {/* فقط للمستخدم العادي (غير الأدمن وغير المسوّق) */}
          {!isAdmin && !isBroker && (
            <ShortcutTile to="/become-broker" icon="🤝" title="كن مسوّقاً" desc="قدّم طلبك للانضمام لشبكة مسوّقي صورة" />
          )}
          {isAdmin && (
            <ShortcutTile to="/admin" icon="🛡️" title="لوحة الإدارة" desc="مستخدمون، عقارات، مزادات، إعدادات" />
          )}
        </div>
      </div>

      {/* صفّان: آخر الإشعارات + آخر العقارات */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* آخر الإشعارات */}
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-black text-navy-dark">🔔 آخر الإشعارات</h2>
              <Link to="/dashboard/notifications" className="text-xs font-bold text-gold hover:underline">
                الكل ←
              </Link>
            </div>
            {notifications.length === 0 ? (
              <div className="text-sm text-gray-400 py-6 text-center">لا توجد إشعارات حتى الآن</div>
            ) : (
              <ul className="space-y-2">
                {notifications.slice(0, 5).map((n) => (
                  <li
                    key={n.id}
                    className={`p-2.5 rounded-lg border text-sm ${
                      n.isRead ? 'border-gray-100 bg-gray-50' : 'border-gold/30 bg-gold/5'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-bold text-navy-dark text-sm flex-1 min-w-0 truncate">{n.title}</div>
                      {!n.isRead && <span className="w-2 h-2 rounded-full bg-gold shrink-0 mt-1.5" />}
                    </div>
                    {n.body && <div className="text-xs text-gray-600 mt-1 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-gray-400 mt-1">
                      {new Date(n.createdAt).toLocaleString('ar-SA')}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* آخر عقاراتي */}
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-black text-navy-dark">🏘️ آخر عقاراتي</h2>
              <Link to="/dashboard/properties" className="text-xs font-bold text-gold hover:underline">
                الكل ←
              </Link>
            </div>
            {myProperties.length === 0 ? (
              <div className="text-sm text-gray-400 py-6 text-center">
                لا توجد عقارات بعد.{' '}
                <Link to="/submit-property" className="text-gold font-bold hover:underline">
                  أضف أول عقار
                </Link>
              </div>
            ) : (
              <ul className="space-y-2">
                {myProperties.slice(0, 5).map((p) => (
                  <li key={p.id} className="p-2.5 rounded-lg border border-gray-100 bg-gray-50 hover:bg-white hover:border-gold/40 transition">
                    <Link to={`/properties/${p.id}`} className="block">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-bold text-navy-dark text-sm flex-1 min-w-0 truncate">{p.title}</div>
                        <Badge color={STATUS_COLORS[p.status] ?? 'gray'}>{STATUS_LABELS[p.status] ?? p.status}</Badge>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-2">
                        <span>📍 {p.city ?? '—'}</span>
                        {p.price != null && (
                          <span>💰 {Number(p.price).toLocaleString('en-US')} ر.س</span>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

    </div>
  );
}
